<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Calendar } from "@lucide/vue";

import { v4 as uuidv4 } from "uuid";

import InputLabel from "./InputLabel.vue";
import { useListboxNavigation } from "./useListboxNavigation.composable";

const props = defineProps<{
  value: number | null;
  label?: string;
  color: string;
  errorMsg?: string;
  required?: boolean;
  disabled?: boolean;
  tooltip?: string;
  markdownPath?: string;
}>();

const emit = defineEmits<{
  "update:value": [value: number | null];
  newSelected: [year: number];
}>();

/** 2023 is the earliest year the wizard accepts; the span is wide enough that nothing has to fall outside it. */
const YEARS: number[] = Array.from({ length: 100 }, (_, index) => 2023 + index);

const field = ref<HTMLInputElement | null>(null);
const localValue = ref<string>("");
const localId = ref<string>(uuidv4());

const listId = computed(() => `${localId.value}-listbox`);
const errorId = computed(() => `${localId.value}-error`);
const optionId = (year: number) => `${localId.value}-option-${year}`;

/**
 * The stored value drives the field.
 *
 * It used to be copied in on mount and never again, and picking a year from the list only emits — it does not
 * touch `localValue`. So the only way a parent could make a pick appear was to force a remount, which is
 * exactly what ProjectDetails did with two `:key` counters. Following the prop instead means a pick shows
 * because the value changed, and a parent that refuses the pick shows nothing, which is the behaviour the
 * commissioning-year confirmation needs and had to fake.
 */
watch(
  () => props.value,
  (value) => {
    localValue.value = value === null || value === undefined ? "" : String(value);
  },
  { immediate: true }
);

/**
 * Only what the user typed narrows the list. A year already in the field is a value, not a query, so
 * opening on 2024 must still offer every other year — filtering on it left one row and no way to
 * change your mind without clearing the field first.
 */
const isFiltering = ref<boolean>(false);

const filteredYears = computed(() => {
  const typed = localValue.value.trim();
  if (!isFiltering.value || typed === "") return YEARS;
  return YEARS.filter((year) => String(year).startsWith(typed));
});

const { root, activeIndex, isListVisible, activeOptionId, open, close, commit, setActive, handleKeydown } =
  useListboxNavigation<number>({
    items: filteredYears,
    optionId,
    onCommit: (year) => emit("newSelected", year),
    onClose: () => (isFiltering.value = false),
    isDisabled: () => props.disabled === true,
    // Start on the year already in the field, so arrowing continues from where the value is.
    activeOnOpen: () => filteredYears.value.findIndex((year) => String(year) === localValue.value),
  });

/**
 * Clicking the field opens the list.
 *
 * `focus` alone is not enough: it fires once, so after an Escape or a pick — both of which leave the
 * input focused — clicking it again fired nothing and the list would not come back until the user
 * clicked away and returned.
 */
const onFieldClick = () => open();

/**
 * Keyed to what is on SCREEN, not to `isOpen`.
 *
 * A query matching no year leaves the list empty and hidden while `isOpen` is still true, so a
 * toggle reading `isOpen` spent its first click closing something invisible and looked broken. The
 * button is labelled "Show years", so opening from it also drops a stale query.
 */
const onToggleClick = () => {
  if (isListVisible.value) {
    close();
  } else {
    isFiltering.value = false;
    open();
  }
  // `tabindex="-1"` keeps the button out of the tab order, and its `mousedown` is prevented so focus
  // never leaves the field — but a keyboard-driven click can still land here, and the keydown
  // handler is on the input, so an open list would otherwise be keyboard-inert.
  field.value?.focus();
};

const onInput = () => {
  // Clamp to four digits in one step. The old `slice(0, -1)` dropped a single character, which is
  // correct for one keystroke but leaves a pasted or autofilled string still over length.
  const digits = localValue.value.replace(/\D/g, "").slice(0, 4);
  localValue.value = digits;
  setActive(-1);
  isFiltering.value = true;
  open();
  emit("update:value", digits === "" ? null : Number(digits));
};

const color = computed(() => {
  if (props.color === "primary") return "focus:border-app-blue-600";
  return undefined;
});
</script>

<template>
  <div
    ref="root"
    class="flex w-full flex-col">
    <InputLabel
      :id="localId"
      :label="label"
      :required="required"
      :markdown-path="markdownPath"
      :tooltip="tooltip" />
    <div class="relative">
      <input
        :id="localId"
        ref="field"
        v-model="localValue"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isListVisible"
        :aria-controls="isListVisible ? listId : undefined"
        :aria-activedescendant="activeOptionId"
        :aria-invalid="errorMsg ? true : undefined"
        :aria-describedby="errorMsg ? errorId : undefined"
        :aria-required="required ? true : undefined"
        :disabled="disabled"
        class="focus-input rounded-8 h-9 w-full border py-2 pr-9 pl-3 text-sm leading-none"
        :class="[
          color,
          errorMsg ? 'border-app-red-500' : 'border-slate-300',
          disabled ? 'text-type-muted interactive-disabled bg-slate-200' : 'bg-slate-0',
        ]"
        @focus="open"
        @click="onFieldClick"
        @input="onInput"
        @keydown="handleKeydown" />

      <button
        type="button"
        tabindex="-1"
        class="focus-ring rounded-4 absolute top-1/2 right-2 -translate-y-1/2 p-1"
        :class="{ 'interactive-disabled': disabled }"
        :disabled="disabled"
        aria-label="Show years"
        @mousedown.prevent
        @click="onToggleClick">
        <Calendar
          class="text-type-muted icon-sm"
          :stroke-width="1.5"
          aria-hidden="true" />
      </button>

      <Transition name="fade-top-quick">
        <ul
          v-if="isListVisible"
          :id="listId"
          role="listbox"
          :aria-label="label ?? 'Years'"
          class="rounded-8 bg-slate-0 shadow-dropdown z-dropdown absolute mt-1 flex max-h-44 w-full flex-wrap overflow-y-auto border border-slate-200">
          <li
            v-for="(year, index) in filteredYears"
            :id="optionId(year)"
            :key="year"
            role="option"
            :aria-selected="index === activeIndex"
            class="duration-fast ease-standard w-1/3 cursor-pointer p-2 text-sm transition-colors"
            :class="index === activeIndex ? 'bg-app-blue-50' : 'hover:bg-slate-100'"
            @mousedown.prevent
            @mouseenter="activeIndex = index"
            @click="commit(year)">
            {{ year }}
          </li>
        </ul>
      </Transition>
    </div>
    <span
      v-if="errorMsg"
      :id="errorId"
      class="text-app-red-700 text-xs"
      >{{ errorMsg }}</span
    >
  </div>
</template>
